import { useQuery } from "@tanstack/react-query";
import { Check, Octagon } from "lucide-react";
import { useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  StatusBadge,
  Textarea,
  Drawer,
  cn,
} from "../../shared/ui";
import type { BlockerView } from "../../shared/types/api";
import { SEVERITY_LABELS, SEVERITY_TONE, formatAge } from "./workItemVocabulary";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Impediments (§86).
 *
 * <p>Age is the field that matters and it leads the row. A blocker's severity is stated once when
 * it is raised and rarely revisited; how long it has been sitting there is the number that should
 * make somebody act, and it is the one nobody updates because it updates itself.
 */
export function WorkItemBlockers({
  workItemId,
  canEdit,
  onAdd,
  onChanged,
}: {
  workItemId: string;
  canEdit: boolean;
  onAdd: () => void;
  onChanged: () => void;
}) {
  const [resolving, setResolving] = useState<BlockerView | null>(null);

  const blockers = useQuery({
    queryKey: ["work-item-blockers", workItemId],
    queryFn: () => api.get<BlockerView[]>(`/api/v1/work-items/${workItemId}/blockers`),
  });

  if (blockers.isError) {
    return <ErrorState error={blockers.error} onRetry={() => void blockers.refetch()} />;
  }

  const rows = blockers.data ?? [];
  const open = rows.filter((blocker) => blocker.status === "OPEN");

  if (rows.length === 0 && !canEdit) {
    return null;
  }

  return (
    <>
      <Card className={cn(open.length > 0 && "border-red-200")}>
        <CardHeader
          title="Blockers"
          subtitle={
            open.length > 0
              ? `${open.length} still in the way`
              : "Nothing currently in the way."
          }
          action={
            canEdit && (
              <Button
                variant="ghost"
                size="sm"
                icon={<Octagon className="h-4 w-4" />}
                onClick={onAdd}
              >
                Raise
              </Button>
            )
          }
        />
        <CardBody className={rows.length === 0 ? undefined : "p-0"}>
          {rows.length === 0 ? (
            <p className="text-sm text-ink-500">
              Nothing raised. A blocker is anything standing in the way — including things that
              are not work items at all, like an access request nobody has approved.
            </p>
          ) : (
            <ul>
              {rows.map((blocker) => (
                <li
                  key={blocker.id}
                  className="flex flex-wrap items-start gap-3 border-b border-ink-100 px-5 py-3 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={cn(
                          "text-sm font-medium",
                          blocker.status === "OPEN"
                            ? "text-ink-900"
                            : "text-ink-400 line-through",
                        )}
                      >
                        {blocker.title}
                      </span>
                      <StatusBadge tone={SEVERITY_TONE[blocker.severity]}>
                        {SEVERITY_LABELS[blocker.severity]}
                      </StatusBadge>
                      {blocker.blockingWorkItemKey && (
                        <span className="font-mono text-xs text-ink-500">
                          {blocker.blockingWorkItemKey}
                        </span>
                      )}
                      {blocker.overdue && (
                        <StatusBadge tone="red">Past its expected date</StatusBadge>
                      )}
                    </div>
                    {blocker.description && (
                      <p className="mt-0.5 text-sm text-ink-500">{blocker.description}</p>
                    )}
                    <p className="mt-1 text-xs text-ink-400">
                      Raised {blocker.raisedDate}
                      {blocker.ownerName ? ` · chased by ${blocker.ownerName}` : " · nobody owns it"}
                      {blocker.expectedResolution
                        ? ` · expected ${blocker.expectedResolution}`
                        : ""}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-3">
                    <span
                      className={cn(
                        "tnum text-sm font-medium",
                        blocker.status !== "OPEN"
                          ? "text-ink-400"
                          : blocker.ageDays >= 7
                            ? "text-red-600"
                            : "text-amber-600",
                      )}
                      title={
                        blocker.status === "OPEN"
                          ? "How long this has been in the way"
                          : "How long it was in the way"
                      }
                    >
                      {formatAge(blocker.ageDays)}
                    </span>
                    {canEdit && blocker.status === "OPEN" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Check className="h-3.5 w-3.5" />}
                        onClick={() => setResolving(blocker)}
                      >
                        Cleared
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <ResolveBlockerDrawer
        blocker={resolving}
        onClose={() => setResolving(null)}
        onSaved={() => {
          void blockers.refetch();
          onChanged();
        }}
      />
    </>
  );
}

/**
 * Records a blocker as cleared.
 *
 * <p>Asks for the day it actually cleared rather than assuming today. Blockers are typically
 * noticed as resolved a day or two after the fact, and defaulting silently would overstate every
 * delay the organisation measures.
 */
function ResolveBlockerDrawer({
  blocker,
  onClose,
  onSaved,
}: {
  blocker: BlockerView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [resolvedOn, setResolvedOn] = useState(today());
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!blocker) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/blockers/${blocker.id}/resolve`, {
        resolvedOn,
        note: note || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that.");
    } finally {
      setSaving(false);
    }
  };

  const tooEarly = blocker != null && resolvedOn < blocker.raisedDate;

  return (
    <Drawer
      open={Boolean(blocker)}
      onClose={onClose}
      title="Blocker cleared"
      subtitle={blocker?.title}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={tooEarly}>
            {saving ? "Saving…" : "Record as cleared"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field
          label="Cleared on"
          required
          hint="The day it actually cleared, not today — the difference is time this project lost."
        >
          <Input
            type="date"
            value={resolvedOn}
            min={blocker?.raisedDate}
            onChange={(event) => setResolvedOn(event.target.value)}
          />
        </Field>

        {tooEarly && (
          <InlineAlert tone="warning">
            It was raised on {blocker?.raisedDate}, so it cannot have cleared before then.
          </InlineAlert>
        )}

        <Field label="What unblocked it" hint="Optional, and useful the next time this happens.">
          <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}
